//
// Copyright 2023 DXOS.org
//

import type { ComponentType, FC, PropsWithChildren, ReactNode } from 'react';

import type { DXN } from '@dxos/keys';
import { log } from '@dxos/log';
import { MakeOptional } from '@dxos/util';
import * as Position from '@dxos/util/Position';

import * as Role from '../../../common/Role.ts';

/**
 * One entry in a {@link Filter} — a role NSID plus the guard that validates the
 * data shape when the Surface dispatcher is matching that role.
 */
export type Binding = {
  readonly role: string;
  readonly guard: (data: unknown) => boolean;
};

/**
 * Typed filter binding for {@link create}. Combines one or more `(role, guard)`
 * pairs so a provider can register against multiple roles while keeping the
 * role and its data shape in a single expression.
 */
export type Filter<TData> = {
  readonly bindings: ReadonlyArray<Binding>;
  /** Covariant phantom; never accessed at runtime. */
  readonly _phantom?: TData;
};

/**
 * Runtime guard for {@link Filter}. Distinguishes new-style filter bindings from
 * legacy predicate filters.
 */
export const isFilter = (value: unknown): value is Filter<any> =>
  typeof value === 'object' && value !== null && Array.isArray((value as { bindings?: unknown }).bindings);

/**
 * Creates a {@link Filter} from a role token and an optional guard.
 *
 * When `guard` is omitted the filter matches any data at the token's role
 * (role-only dispatch). Pass a guard to add runtime data-shape validation on
 * top of the role match.
 *
 * This is the framework-level primitive; `@dxos/app-toolkit` builds richer
 * domain-aware helpers (ECHO schema checks, literal matching, etc.) on top of it.
 */
export const makeFilter = <TData>(token: Role.Role<TData>, guard?: (data: TData) => boolean): Filter<TData> => {
  const boundGuard =
    guard == null
      ? () => true
      : (data: unknown): boolean => {
          try {
            return guard(data as TData);
          } catch (err) {
            log.catch(err);
            return false;
          }
        };
  return { bindings: [{ role: token.role, guard: boundGuard }] };
};

/**
 * Typed Surface consumer props — carries the role/data-shape contract via a
 * {@link Role.Role}. The `type` prop is required; the string `role` prop has
 * been removed from the consumer-facing API.
 */
export type Props<T extends Record<string, any> = Record<string, unknown>> = {
  /**
   * If specified, the Surface will be wrapped in an error boundary.
   * The fallback component will be rendered if an error occurs.
   */
  fallback?: FC<{ error: Error; data?: any }>;

  /**
   * If specified, the Surface will be wrapped in a suspense boundary.
   * The placeholder component will be rendered while the surface component is loading.
   */
  placeholder?: ReactNode;
} & MakeOptional<CoreProps<T>, 'id' | 'data'>;

/**
 * Typed Surface consumer props — carries the role/data-shape contract via a
 * {@link Role.Role}. Available as a typed overload of the Surface component so
 * ad-hoc `type` fields on arbitrary spread props don't conflict with the
 * untyped consumer form.
 */
export type TypedProps<TToken extends Role.Role<any>> = {
  fallback?: FC<{ error: Error; data?: any }>;
  placeholder?: ReactNode;
  id?: string;
  type: TToken;
  data?: Role.Data<TToken>;
  limit?: number | undefined;
} & {
  /**
   * Additional pass-through props. Known prop names are excluded so the
   * catch-all doesn't widen `data` / `type` / etc. to `any` at the intersection.
   */
  [K in keyof Record<string, any>]: K extends 'fallback' | 'placeholder' | 'id' | 'type' | 'data' | 'limit'
    ? never
    : any;
};

/**
 * NOTE: If `[key: string]: unknown` is included in shared types, when re-used other fields become unknown as well.
 * The `type` prop is the only way to set the role; it is required here and
 * optional in {@link Props} via MakeOptional.
 */
export type CoreProps<T extends Record<string, any> = Record<string, unknown>> = PropsWithChildren<{
  /**
   * ID for debugging.
   */
  id: string;

  /**
   * The role token defining how the data should be rendered.
   */
  type: Role.Role<T>;

  /**
   * The data to be rendered by the surface.
   * NOTE: This must be a stable value.
   */
  data: T;

  /**
   * If more than one component is resolved, the limit determines how many are rendered.
   */
  limit?: number | undefined;
}>;

export type ComponentProps<T extends Record<string, any> = Record<string, any>> = {
  /**
   * The resolved role NSID string (e.g. `org.dxos.role.article`). Present on
   * matched components only — multi-role containers can branch on this value
   * by comparing against a token's `.role` (e.g. `role === AppSurface.Section.role`).
   */
  role: string;
} & PropsWithChildren<{
  id: string;
  data: T;
  limit?: number | undefined;
}> &
  Record<string, any>;

/**
 * React component used to render a surface once is has matched.
 */
export type ComponentFunction<T extends Record<string, any> = Record<string, any>> = (
  props: ComponentProps<T>,
) => ReactNode;

/**
 * Definition of when a React component surface should be rendered.
 *
 * The optional `filter` receives the consumer-supplied `role` as its second
 * argument so a single definition can encode role-specific guards (used by the
 * {@link Filter}-based `create` overload).
 *
 * @internal The `role` field is populated by the framework from the filter
 *   bindings and is not authored directly.
 */
export type ReactDefinition<T extends Record<string, any> = any> = Readonly<{
  kind: 'react';
  id: string;
  role: string | string[];
  position?: Position.Position;
  component: ComponentType<any>;
  /** Maps the surface props onto the component's props; see {@link TypedReactDefinition.props}. */
  props?: (props: ComponentProps<T>) => Record<string, any>;
  filter?: (data: Record<string, unknown>, role?: string) => data is T;
}>;

/**
 * Definition of when a Web Component surface should be rendered.
 *
 * @internal The `role` field is populated by the framework from the filter
 *   bindings and is not authored directly.
 */
export type WebComponentDefinition<T extends Record<string, any> = any> = Readonly<{
  kind: 'web-component';
  id: string;
  role: string | string[];
  position?: Position.Position;
  /**
   * The tag name of the Web Component to render.
   * The Web Component will receive the same props as React surfaces via properties/attributes.
   */
  tagName: string;
  filter?: (data: Record<string, unknown>, role?: string) => data is T;
}>;

/**
 * Definition of when a surface (React or Web Component) should be rendered.
 */
export type Definition<T extends Record<string, any> = any> = ReactDefinition<T> | WebComponentDefinition<T>;

/**
 * Typed React surface definition — role is derived from the filter's bindings.
 */
export type TypedReactDefinition<
  T extends Record<string, any> = any,
  P extends Record<string, any> = ComponentProps<T>,
  Id extends string = string,
> = Readonly<{
  id: [DXN.Path<Id>] extends [never]
    ? `Invalid id "${Id}": final segment must be camelCase — letters and digits, starting with a letter`
    : Id;
  filter: Filter<T>;
  position?: Position.Position;
  /**
   * Accepts any component type (not just a function) so a container re-exported through a
   * `lazy()` barrel as `ComponentType<any>` can be registered without a cast.
   */
  component: ComponentType<P>;
  /**
   * Maps the surface props onto the component's own props, so a plain container can be registered
   * directly instead of being wrapped in an adapter that unpacks `data`.
   *
   * Prefer this over an inline `component: ({ data }) => <Container … />`: the mapper's input type
   * derives from the same `filter` that defines the surface's data shape, so the unpacking is
   * checked against the filter rather than restated by hand.
   *
   * @example
   * Surface.create({
   *   id: 'defaultPluginSettings',
   *   filter: AppSurface.settings(AppSurface.Article),
   *   component: DefaultSettings,
   *   props: ({ data: { subject } }) => ({ subject }),
   * });
   */
  props?: (props: ComponentProps<T>) => P;
}>;

/**
 * Typed Web Component surface definition.
 */
export type TypedWebComponentDefinition<T extends Record<string, any> = any, Id extends string = string> = Readonly<{
  id: [DXN.Path<Id>] extends [never]
    ? `Invalid id "${Id}": final segment must be camelCase — letters and digits, starting with a letter`
    : Id;
  filter: Filter<T>;
  tagName: string;
  position?: Position.Position;
}>;

const expandBindings = <T extends Record<string, any>>(
  filter: Filter<T>,
): { role: string | string[]; guard: (data: Record<string, unknown>, role?: string) => data is T } => {
  const bindings = filter.bindings;
  const roles = Array.from(new Set(bindings.map((binding) => binding.role)));
  const guard = (data: Record<string, unknown>, role?: string): data is T => {
    if (role != null) {
      // Multiple bindings may share a role (e.g. via `oneOf` of same-role filters);
      // the role matches if ANY of them passes.
      return bindings.some((entry) => entry.role === role && entry.guard(data));
    }
    return bindings.some((entry) => entry.guard(data));
  };
  return { role: roles.length === 1 ? roles[0] : roles, guard };
};

/**
 * Creates a React surface definition from a typed filter.
 */
export function create<
  T extends Record<string, any> = any,
  P extends Record<string, any> = ComponentProps<T>,
  const Id extends string = string,
>(definition: TypedReactDefinition<T, P, Id>): ReactDefinition<T>;
export function create<
  T extends Record<string, any> = any,
  P extends Record<string, any> = ComponentProps<T>,
  const Id extends string = string,
>(definition: TypedReactDefinition<T, P, Id>): ReactDefinition<T> {
  const { id, filter, component, props, position } = definition;
  const { role, guard } = expandBindings(filter);
  return { kind: 'react', id, role, position, component, props, filter: guard };
}

/**
 * Creates a Web Component surface definition from a typed filter.
 */
export function createWeb<T extends Record<string, any> = any, const Id extends string = string>(
  definition: TypedWebComponentDefinition<T, Id>,
): WebComponentDefinition<T>;
export function createWeb<T extends Record<string, any> = any, const Id extends string = string>(
  definition: TypedWebComponentDefinition<T, Id>,
): WebComponentDefinition<T> {
  const { id, filter, tagName, position } = definition;
  const { role, guard } = expandBindings(filter);
  return { kind: 'web-component', id, role, position, tagName, filter: guard };
}
